import {
  Activity,
  ArrowDownToLine,
  ArrowLeftRight,
  Award,
  BadgeCheck,
  BadgeDollarSign,
  Banknote,
  Bell,
  BellRing,
  Bike,
  Blocks,
  BookOpen,
  Boxes,
  BriefcaseBusiness,
  Building2,
  Calculator,
  Car,
  ChartColumn,
  ChartPie,
  CirclePlus,
  ClipboardList,
  Coins,
  ContactRound,
  CreditCard,
  FileBarChart,
  FileDown,
  FilePlus,
  FileText,
  FileUp,
  FolderOpen,
  FolderPlus,
  FolderTree,
  Gift,
  HandCoins,
  HeartPulse,
  Image,
  Images,
  Landmark,
  Languages,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  List,
  Mail,
  MapPin,
  Megaphone,
  MessageSquare,
  Package,
  PackageOpen,
  PackageSearch,
  PanelsTopLeft,
  Percent,
  Plug,
  Puzzle,
  ReceiptText,
  RotateCcw,
  Ruler,
  Scale,
  Settings,
  Settings2,
  Share2,
  Shield,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Star,
  Store,
  Tags,
  Target,
  TicketPercent,
  TrendingDown,
  TrendingUp,
  Truck,
  UsersRound,
  Wallet,
  WalletCards,
  XCircle,
  Zap,
  type LucideIcon,
} from 'lucide-react';

/**
 * Central navigation icon registry.
 * One source of truth for desktop sidebar, collapsed sidebar, mobile, and tooltips.
 * Names are semantic — pick by route meaning, not by visual preference.
 */
export type IconName =
  | 'activity'
  | 'arrow-down-to-line'
  | 'arrow-left-right'
  | 'award'
  | 'badge-check'
  | 'badge-dollar-sign'
  | 'banknote'
  | 'bell'
  | 'bell-ring'
  | 'bike'
  | 'blocks'
  | 'book-open'
  | 'boxes'
  | 'briefcase-business'
  | 'building-2'
  | 'calculator'
  | 'car'
  | 'chart-column'
  | 'chart-pie'
  | 'circle-plus'
  | 'clipboard-list'
  | 'coins'
  | 'contact-round'
  | 'credit-card'
  | 'file-chart'
  | 'file-down'
  | 'file-plus'
  | 'file-text'
  | 'file-up'
  | 'folder-open'
  | 'folder-plus'
  | 'folder-tree'
  | 'gift'
  | 'grid'
  | 'hand-coins'
  | 'heart-pulse'
  | 'image'
  | 'images'
  | 'landmark'
  | 'languages'
  | 'layout-dashboard'
  | 'layout-template'
  | 'list'
  | 'mail'
  | 'map-pin'
  | 'megaphone'
  | 'message'
  | 'package'
  | 'package-open'
  | 'package-search'
  | 'panels'
  | 'percent'
  | 'plug'
  | 'puzzle'
  | 'receipt'
  | 'rotate-ccw'
  | 'ruler'
  | 'scale'
  | 'settings'
  | 'settings-2'
  | 'share-2'
  | 'shield'
  | 'shield-check'
  | 'shopping-cart'
  | 'smartphone'
  | 'star'
  | 'store'
  | 'tags'
  | 'target'
  | 'ticket-percent'
  | 'trending-down'
  | 'trending-up'
  | 'truck'
  | 'users-round'
  | 'wallet'
  | 'wallet-cards'
  | 'x-circle'
  | 'zap'
  // legacy aliases kept so older nav strings keep resolving
  | 'home'
  | 'cart'
  | 'layers'
  | 'gift'
  | 'tv'
  | 'tag'
  | 'user'
  | 'users'
  | 'cash'
  | 'chart'
  | 'package'
  | 'email'
  | 'car'
  | 'briefcase'
  | 'user-plus'
  | 'user-minus'
  | 'user-x'
  | 'users-round';

const ICONS: Record<string, LucideIcon> = {
  // semantic
  activity: Activity,
  'arrow-down-to-line': ArrowDownToLine,
  'arrow-left-right': ArrowLeftRight,
  award: Award,
  'badge-check': BadgeCheck,
  'badge-dollar-sign': BadgeDollarSign,
  banknote: Banknote,
  bell: Bell,
  'bell-ring': BellRing,
  bike: Bike,
  blocks: Blocks,
  'book-open': BookOpen,
  boxes: Boxes,
  'briefcase-business': BriefcaseBusiness,
  'building-2': Building2,
  calculator: Calculator,
  car: Car,
  'chart-column': ChartColumn,
  'chart-pie': ChartPie,
  'circle-plus': CirclePlus,
  'clipboard-list': ClipboardList,
  coins: Coins,
  'contact-round': ContactRound,
  'credit-card': CreditCard,
  'file-chart': FileBarChart,
  'file-down': FileDown,
  'file-plus': FilePlus,
  'file-text': FileText,
  'file-up': FileUp,
  'folder-open': FolderOpen,
  'folder-plus': FolderPlus,
  'folder-tree': FolderTree,
  gift: Gift,
  'hand-coins': HandCoins,
  'heart-pulse': HeartPulse,
  image: Image,
  images: Images,
  landmark: Landmark,
  languages: Languages,
  'layout-dashboard': LayoutDashboard,
  'layout-template': LayoutTemplate,
  list: List,
  mail: Mail,
  'map-pin': MapPin,
  megaphone: Megaphone,
  message: MessageSquare,
  package: Package,
  'package-open': PackageOpen,
  'package-search': PackageSearch,
  panels: PanelsTopLeft,
  percent: Percent,
  plug: Plug,
  puzzle: Puzzle,
  receipt: ReceiptText,
  'rotate-ccw': RotateCcw,
  ruler: Ruler,
  scale: Scale,
  settings: Settings,
  'settings-2': Settings2,
  'share-2': Share2,
  shield: Shield,
  'shield-check': ShieldCheck,
  'shopping-cart': ShoppingCart,
  smartphone: Smartphone,
  star: Star,
  store: Store,
  tags: Tags,
  target: Target,
  'ticket-percent': TicketPercent,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  truck: Truck,
  'users-round': UsersRound,
  wallet: Wallet,
  'wallet-cards': WalletCards,
  'x-circle': XCircle,
  zap: Zap,

  // legacy aliases
  home: LayoutDashboard,
  cart: ShoppingCart,
  layers: Target,
  tv: Megaphone,
  tag: Tags,
  user: ContactRound,
  users: UsersRound,
  cash: Wallet,
  chart: ChartColumn,
  email: Mail,
  briefcase: BriefcaseBusiness,
  'user-plus': CirclePlus,
  'user-minus': XCircle,
  'user-x': XCircle,
  grid: LayoutGrid,
};

export function NavIcon({
  name,
  size = 20,
  strokeWidth = 1.8,
}: {
  name: IconName | string;
  size?: number;
  strokeWidth?: number;
}) {
  const Icon = ICONS[name] ?? LayoutGrid;
  return <Icon size={size} strokeWidth={strokeWidth} aria-hidden className="nav-svg-icon" />;
}

/** Default icon sizes by surface — keep recognition high without clutter. */
export const NAV_ICON_SIZE = {
  expanded: 20,
  collapsed: 24,
  mobile: 20,
  section: 20,
} as const;
